import { Box, Card, CardContent, Grid, Stack, Tooltip, Typography } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import i18n from 'simple-react-i18n'
import PropTypes from 'prop-types'
import { compact, groupBy, isNil, isNull, isUndefined, keys, max, maxBy, meanBy, orderBy, round, sumBy, template, uniq, uniqBy } from 'lodash'
import CartographyPanel from 'components/map/CartographyPanel'
import { THRESHOLD_COLOR, THRESHOLD_COLOR_BY_SIZE } from 'utils/QualityThresholdUtils'
import { getColorCircleElement } from 'utils/ColorUtil'
import { blackColor, darkBlue, disabledBorderInputColor, PASTEL_COLOR, whiteColor } from 'utils/constants/ColorTheme'
import useApplicationSetting, { floatParser, intParser, listStringParser } from 'utils/customHook/useApplicationSetting'
import RadioButtons from 'components/forms/RadioButtons'
import { CardTitle } from 'components/card/NewCard'
import { calculatePercentile90, calculateValue, REMARK_CODES, searchMaxAnalysis } from 'utils/AnalyseUtils'
import { AVERAGE_SUBSTANCE, AVERAGE_SUM, DEFAULT_REGROUPMENT, DEFAULT_THRESHOLD_NITRATES, DEFAULT_THRESHOLD_PESTICIDES_1, DEFAULT_THRESHOLD_PESTICIDES_2, MAX_SUBSTANCE, MAX_SUM, MISSING_DATA, NITRATES, NITRATES_CODE, NITRATES_PESTICIDES, NO_CONTAMINATION, PESTICIDES } from 'catchment/constants/CatchmentConstant'
import DtoAnalysisUltraLight from 'quality/dto/analyse/DtoAnalysisUltraLight'
import useEndPeriod from 'catchment/utils/useEndPeriod'
import moment from 'moment'
import { getYear } from 'utils/DateUtil'
import CatchmentDto from 'catchment/dto/CatchmentDto'
import useBoolean from 'utils/customHook/useBoolean'
import DtoFile from 'components/file/dto/DtoFile'
import { DOCUMENT } from 'components/file/constant/FileConstants'
import ToastrAction from 'toastr/actions/ToastrAction'
import { getFileExtension, getFileName, getFormatedFileName } from 'utils/FileUtils'
import { STATION_TYPE_NAME } from 'station/constants/StationConstants'
import CatchmentAction from 'catchment/actions/CatchmentAction'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import ProgressCard from 'components/card/ProgressCard'
import CatchmentPointDto from 'catchment/dto/CatchmentPointDto'
import { QualityThresholdType } from 'quality/shapes/QualityThresholdShapes'
import Select from 'components/forms/Select'
import { LAYERS } from 'components/map/constants/MapLayersConstants'
import { getContaminationState, getNitratesState, getPesticidesState } from 'catchment/utils/CatchmentUtil'
import { getI18nOrLabel } from 'utils/StringUtil'
import InfoIcon from '@mui/icons-material/Info'

const DEFAULT_NITRATE_THRESHOLDS = [2, 10, 25, 50]
const DEFAULT_PESTICIDES_DISTRIBUTION_THRESHOLDS = [0.1, 2]
const DEFAULT_SUM_PESTICIDES_DISTRIBUTION_THRESHOLDS = [0.5, 5]

const NO_ANALYSIS_VALUE = '-1'
const SORTING_TYPE = {
    LAST_VALUE: '0',
    MAX: '1',
    AVERAGE: '3',
    P90: '4',
    FIX: '5',
}
const VIEW = {
    CATCHMENTS: 0,
    QUALITOMETERS: 1,
}
const CRITERIA = {
    PROBLEMATIC: 0,
    THRESHOLDS: 1,
}
const IS_ACTIVE = '1'
const CARTO_MNEMONIQUE = 'CAR'

const ViewFormatter = ({
    title = '',
    helper = '',
    labels = [],
    colors = [],
}) => (
    labels.length > colors.length ?
        <Typography>{title}</Typography> :
        (
            <Stack spacing={1}>
                <Grid container spacing={1}>
                    <Grid size={'grow'}>
                        <Typography>
                            {title}
                        </Typography>
                    </Grid>
                    <Grid size={'auto'}>
                        <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{helper}</span>}>
                            <InfoIcon />
                        </Tooltip>
                    </Grid>
                </Grid>

                {labels.map((l, i) => (
                    <Box key={l} sx={{ pl: 2 }}>
                        {getColorCircleElement(colors[i], true)}
                        {labels[i]}
                    </Box>
                ))}
            </Stack>
        )
)

ViewFormatter.propTypes = {
    title: PropTypes.string,
    helper: PropTypes.string,
    labels: PropTypes.arrayOf(PropTypes.string),
    colors: PropTypes.arrayOf(PropTypes.string),
}

const CriteriaPanel = ({
    title = '',
    criteriaValue,
    onChangeCriteriaValue = () => { },
    mapView,
    onChangeMapView = () => {},
    criterias = [],
    views = [],
}) => {
    return (
        <Card>
            <CardTitle
                title={title}
                color={darkBlue}
            />
            <CardContent>
                <Stack sx={{ height: '350px' }} spacing={1}>
                    <Select
                        options={views}
                        label={i18n.displayFor}
                        value={mapView}
                        onChange={onChangeMapView}
                        noNullValue
                    />
                    <RadioButtons
                        elements={ criterias }
                        selected={ criteriaValue }
                        onChange={ v => onChangeCriteriaValue(v) }
                    />
                </Stack>
            </CardContent>
        </Card>
    )
}

CriteriaPanel.propTypes = {
    title: PropTypes.string,
    criteriaValue: PropTypes.number,
    onChangeCriteriaValue: PropTypes.func,
    mapView: PropTypes.number,
    onChangeMapView: PropTypes.func,
    criterias: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.element,
        value: PropTypes.number,
    })),
    views: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.number,
    })),
}

const analysisFormatter = (analysis = [], thresholdsData = {}) => {
    const { thresholds, colors } = thresholdsData
    if (!(analysis?.length > 0 && thresholds?.length > 0 && colors.length >= thresholds.length)) {
        return analysis.map(a => ({ ...a, value: calculateValue(a), color: THRESHOLD_COLOR.white }))
    }
    return analysis.map(a => {
        const thresholdIndex = thresholds.indexOf(thresholds.find(t =>
            t >= a.result ||
            (t === getI18nOrLabel(a.contamination) && !isNull(t)) ||
            (t === a.thresholdExceeding && !isNull(t)),
        ))
        const threshold = thresholdIndex === -1 ? thresholds.length : thresholdIndex
        switch (a.remark) {
            case REMARK_CODES.NO_ANALYSIS:
                return {
                    ...a,
                    value: '',
                    color: THRESHOLD_COLOR.white,
                }
            case REMARK_CODES.INFERIOR_LD:
            case REMARK_CODES.TRACES:
            case REMARK_CODES.INFERIOR_VALUE:
            case REMARK_CODES.INFERIOR_LQ:
                return {
                    ...a,
                    value: !isNil(a.result) ? `<${round(a.result, 5)}` : '',
                    color: colors[threshold],
                }
            case REMARK_CODES.SUPERIOR_VALUE:
            case REMARK_CODES.SATURATION:
                return {
                    ...a,
                    value: !isNil(a.result) ? `>${round(a.result, 5)}` : '',
                    color: colors[threshold],
                }
            default:
                return {
                    ...a,
                    remark: a.remark,
                    value: !isNil(a.result) ? `${round(a.result, 5)}` : '',
                    color: colors[threshold],
                }
        }
    })
}

const NitratesOverviewMap = ({
    analysis = [],
    useP90 = false,
    qualitoIds = [],
    catchments = [],
    catchmentPoints = [],
}) => {
    const dispatch = useDispatch()

    const {
        qualitometers,
    } = useSelector(store => ({
        qualitometers: store.QualityReducer.qualitometersLight,
    }), shallowEqual)

    const endPeriod = useEndPeriod(intParser)
    const regroupment = useApplicationSetting('CATCHMENT_REGROUPING', setting => setting ? parseInt(setting) : DEFAULT_REGROUPMENT)

    const thresholdNitrates = useApplicationSetting('CATCHMENT_NITRATES_THRESHOLD', setting => setting ? parseFloat(setting) : DEFAULT_THRESHOLD_NITRATES)
    const threshold1 = useApplicationSetting('CATCHMENT_NITRATES_DISTRIBUTION_THRESHOLD_1', floatParser)
    const threshold2 = useApplicationSetting('CATCHMENT_NITRATES_DISTRIBUTION_THRESHOLD_2', floatParser)
    const threshold3 = useApplicationSetting('CATCHMENT_NITRATES_DISTRIBUTION_THRESHOLD_3', floatParser)
    const threshold4 = useApplicationSetting('CATCHMENT_NITRATES_DISTRIBUTION_THRESHOLD_4', floatParser)

    const {
        value: isLoaded,
        setTrue: loaded,
    } = useBoolean(false)

    const [mapView, setMapView] = useState(1)
    const [criteriaValue, setCriteriaValue] = useState(0)
    const [catchmentsFiles, setCatchmentsFiles] = useState([])

    const thresholds = useMemo(() => {
        const thresholdsSettings = [threshold1, threshold2, threshold3, threshold4].filter(t => !isUndefined(t))
        return thresholdsSettings.length ? thresholdsSettings : DEFAULT_NITRATE_THRESHOLDS
    },
    [threshold1, threshold2, threshold3, threshold4])
    const graphThresholds = [{
        parameterCode: NITRATES_CODE,
        threshold1: thresholds?.[0],
        threshold2: thresholds?.[1],
        threshold3: thresholds?.[2],
        threshold4: thresholds?.[3],
    }]
    const thresholdsColors = useMemo(() => (THRESHOLD_COLOR_BY_SIZE[thresholds.length + 1]), [thresholds.length])

    const getName = useCallback(i => {
        const data = useP90 ? i18n.P90 : i18n.max
        if (i === 0) {
            return `${data} <= ${thresholds[i]} mg/l`
        }
        if (i === thresholds.length) {
            return `${data} > ${thresholds[i - 1]} mg/l`
        }
        return `${thresholds[i - 1]} < ${data} <= ${thresholds[i]} mg/l`
    }, [useP90, thresholds])
    const thresholdsNames = useMemo(() => thresholdsColors.map((c, i) => getName(i)), [thresholdsColors, getName])

    const mapViews = [
        { id: VIEW.CATCHMENTS, name: i18n.catchments },
        { id: VIEW.QUALITOMETERS, name: i18n.qualitometers },
    ]

    const criterias = useMemo(() => ([
        { value: CRITERIA.PROBLEMATIC, label: <ViewFormatter
            title={i18n.displayByProblematic}
            helper={template(i18n.nitratesProblematicDetail)({ threshold: `(${thresholdNitrates} mg/L)`, criteria: useP90 ? i18n.P90 : i18n.max })}
            labels={[i18n.nitrates, i18n.noContaminationHigherThanThreshold, i18n.missingData]}
            colors={[PASTEL_COLOR.yellow, PASTEL_COLOR.blue, disabledBorderInputColor]}
        /> },
        { value: CRITERIA.THRESHOLDS, label: <ViewFormatter
            title={`${i18n.displayByThreshold} (${i18n.nitrates})`}
            helper={template(i18n.nitratesThresholdDetail)({ criteria: useP90 ? i18n.P90 : i18n.max })}
            labels={[...thresholdsNames, i18n.missingData]}
            colors={[...thresholdsColors, disabledBorderInputColor]}
        /> },
    ]), [thresholdsNames, thresholdsColors, thresholdNitrates, useP90])

    const usedThresholds = useMemo(() => {
        if (criteriaValue === CRITERIA.PROBLEMATIC) return { thresholds: [thresholdNitrates], colors: [PASTEL_COLOR.blue, PASTEL_COLOR.yellow] }
        if (criteriaValue === CRITERIA.THRESHOLDS) return { thresholds, colors: thresholdsColors }
        return []
    }, [criteriaValue, thresholdNitrates, thresholds, thresholdsColors])

    const formattedAnalysis = useMemo(() => analysisFormatter(analysis, usedThresholds), [analysis, usedThresholds])
    const filteredAnalysis = useMemo(() => {
        const unusedQualitometers = qualitoIds.filter(id => !analysis.some(a => a.qualitometer === id))
        const ghostAnalysis = unusedQualitometers.map(q => ({ qualitometer: q, result: -1, value: NO_ANALYSIS_VALUE, color: disabledBorderInputColor, parameter: NITRATES_CODE }))
        const mapAnalysis = [...formattedAnalysis, ...ghostAnalysis]
        return orderBy(mapAnalysis, 'analysisDate', 'desc')
    }, [qualitoIds, analysis, formattedAnalysis])

    useEffect(() => {
        const sandreCatchments = catchments.filter(c => !isUndefined(c.sandreCode))
        const uniqueSandre = uniq(sandreCatchments.map(c => c.sandreCode)).filter(s => !catchmentsFiles.some(f=>f?.url?.includes(`cdaac_${s}.kml`)))
        const filesPromises = uniqueSandre.map(s => dispatch(CatchmentAction.fetchKML(`cdaac_${s}`)))
        Promise.all(filesPromises)
            .then((files = []) => {
                if (!isUndefined(files)) {
                    const sandreFiles = compact(files).map(f => ({ ...f, sandreCode: f.name?.split('_')?.[1]?.split('.kml')?.[0] }))
                    const formattedFiles = sandreCatchments.reduce((acc, c) => {
                        const catchmentFile = sandreFiles.find(f => f.sandreCode === c.sandreCode?.toString())
                        if (isUndefined(catchmentFile?.name) || isUndefined(catchmentFile?.url)) return acc
                        const now = moment().valueOf()
                        const formattedName = getFormatedFileName(c.code ?? '', IS_ACTIVE, getFileName(catchmentFile.name), getFileExtension(catchmentFile.name), CARTO_MNEMONIQUE, now, STATION_TYPE_NAME.catchment)
                        if (files.some(file => file?.name === formattedName)) {
                            dispatch(ToastrAction.warning(i18n.nameAlreadyExist))
                            return acc
                        }
                        const formattedFile = {
                            code: c.id,
                            url: catchmentFile.url,
                            name: formattedName,
                            stationCode: c.code ?? '',
                        }
                        return [...acc, new DtoFile(formattedFile, DOCUMENT)]
                    }, [])
                    if (formattedFiles.length > 0) setCatchmentsFiles(formattedFiles)
                }
            }).finally(loaded)
    }, [catchments])

    const colorfulFiles = useMemo(() => catchmentsFiles.map(f => {
        const remainingQualitometers = qualitoIds.map(id => qualitometers.find(q => q.id === id))
        const catchment = catchments.find(c => c.id === f.code) || {}
        const points = catchmentPoints.filter(p => p.id === catchment.id)
        const qualito = remainingQualitometers.filter(q => points.map(p => p.codeWithoutDesignation).includes(q.code))
        const catchmentAnalysis = formattedAnalysis.filter(a => qualito.map(q => q.id).includes(a.qualitometer))
        const maxColor = maxBy(catchmentAnalysis, 'result')?.color
        return { ...f, fillColor: maxColor ?? whiteColor, strokeColor: blackColor }
    }), [catchments, catchmentPoints, qualitoIds, qualitometers, formattedAnalysis, catchmentsFiles])

    const panelTitle = useMemo(() => {
        const startYear = getYear(moment(endPeriod, 'YYYY').subtract(regroupment - 1, 'years').startOf('year').valueOf())
        const endYear = getYear(moment(endPeriod, 'YYYY').endOf('year').valueOf())
        if (startYear === endYear) return `${i18n.problematicsPromotion} - ${i18n.year} ${startYear}`
        return `${i18n.problematicsPromotion} - ${i18n.periode} ${startYear}-${endYear}`
    }, [endPeriod, regroupment])
    const height = window.innerHeight / 2

    return (
        <Box sx={{ height: `${height}px` }}>
            {isLoaded && (
                <CartographyPanel
                    layers={[mapView === VIEW.CATCHMENTS ? LAYERS.CATCHMENTS : LAYERS.STATIONS_POINTS]}
                    catchments={catchments}
                    analysis={filteredAnalysis}
                    qualityThresholds={graphThresholds}
                    sortingType={useP90 ? SORTING_TYPE.P90 : SORTING_TYPE.MAX}
                    stationsPanelTitle={mapView === VIEW.CATCHMENTS ? i18n.catchments : i18n.qualitometers}
                    panel={
                        <CriteriaPanel
                            title={panelTitle}
                            criteriaValue={criteriaValue}
                            onChangeCriteriaValue={setCriteriaValue}
                            mapView={mapView}
                            onChangeMapView={setMapView}
                            criterias={criterias}
                            views={mapViews}
                        />
                    }
                    height={height}
                    selectedParameter={NITRATES_CODE}
                    stationFiles={colorfulFiles}
                />
            ) || <ProgressCard indeterminate/>}
        </Box>
    )
}

NitratesOverviewMap.propTypes = {
    analysis: PropTypes.arrayOf(PropTypes.instanceOf(DtoAnalysisUltraLight)),
    useP90: PropTypes.bool,
    qualitoIds: PropTypes.arrayOf(PropTypes.number),
    catchments: PropTypes.arrayOf(PropTypes.instanceOf(CatchmentDto)),
    catchmentPoints: PropTypes.arrayOf(PropTypes.instanceOf(CatchmentPointDto)),
}

const getAnalysisThresholdExceeding = (
    analysis = {},
    thresholds = [],
    specificThresholds,
) => {
    if (!analysis.remark === REMARK_CODES.VALID || !analysis.result) return 'threshold0'
    const paramThreshold = specificThresholds.thresholds?.find(t => t.parameter === analysis.parameter)
    const thresholdsToTest = paramThreshold ? [
        paramThreshold.threshold1 ?? thresholds[0],
        paramThreshold.threshold2 ?? thresholds[1],
    ].filter(t => !isUndefined(t)) : thresholds
    const indiceResult = thresholdsToTest.findIndex(t => analysis.result < t)
    return indiceResult !== -1 ? `threshold${indiceResult}` : `threshold${thresholds.length}`
}

const thresholdColor = (threshold, colors, defaultColor) => {
    switch (threshold) {
        case 'threshold0': return colors[0]
        case 'threshold1': return colors[1]
        case 'threshold2': return colors[2]
        default: return defaultColor
    }
}

const findAnalysisByContamination = (
    checkContamination = false,
    pesticidesAnalysis = [],
    calculationMethod = [],
    listSumPesticides = [],
    thresholdList = [],
    thresholdPesticides1 = 0.1,
    thresholdPesticides2 = 0.5,
) => {
    const consideredCalculationMethods = calculationMethod.length === 0 ? [AVERAGE_SUBSTANCE, MAX_SUM] : calculationMethod
    if (consideredCalculationMethods.includes(MAX_SUBSTANCE)) {
        const groupParameters = groupBy(pesticidesAnalysis, 'parameter')
        const maxByParameter = keys(groupParameters).map(key => {
            const thresholdUsed = thresholdList.thresholds?.find(t => t.parameterCode === key)?.threshold1 ?? thresholdPesticides1
            const parameterAnalysis = maxBy(groupParameters[key], 'result')
            const result = parameterAnalysis?.result
            if (isUndefined(result)) {
                return {}
            }
            const resultNumber = round(result, 4)
            if (resultNumber >= thresholdUsed) return { ...parameterAnalysis, color: PASTEL_COLOR.orange }
            if (!checkContamination) return parameterAnalysis
            return {}
        })
        if (!checkContamination) return { ...maxBy(maxByParameter, 'result'), color: PASTEL_COLOR.blue }
        const maxStation = maxBy(maxByParameter, 'result')
        if (maxStation?.color === PASTEL_COLOR.orange) return { ...maxStation, color: PASTEL_COLOR.orange }
    }
    if (consideredCalculationMethods.includes(AVERAGE_SUBSTANCE)) {
        const groupParameters = groupBy(pesticidesAnalysis, 'parameter')
        const maxByParameter = keys(groupParameters).map(key => {
            const thresholdUsed = thresholdList.thresholds?.find(t => t.parameterCode === key)?.threshold1 ?? thresholdPesticides1
            const nbOperation = uniqBy(groupParameters[key], p => `${p.qualitometer}#${p.operation}`).length
            const analyse = groupParameters[key][0]
            const result = sumBy(groupParameters[key], 'result') / nbOperation
            return {
                result,
                value: `${round(result, 4)}`,
                contamination: result >= thresholdUsed ? PESTICIDES : NO_CONTAMINATION,
                color: checkContamination ? PASTEL_COLOR.orange : PASTEL_COLOR.blue,
                parameter: analyse?.parameter,
                unit: analyse?.unit,
                fraction: analyse?.fraction,
                support: analyse?.support,
                qualitometer: analyse?.qualitometer,
            }
        })
        if (!checkContamination) return maxBy(maxByParameter, 'result')
        const maxStation = maxBy(maxByParameter.filter(a => a?.contamination === PESTICIDES), 'result')
        if (maxStation?.contamination === PESTICIDES) return maxStation
    }
    if (consideredCalculationMethods.includes(MAX_SUM) || consideredCalculationMethods.includes(AVERAGE_SUM)) {
        const pesticidesFiltered = listSumPesticides.length ? pesticidesAnalysis.filter(a => listSumPesticides.includes(a.parameter)) : pesticidesAnalysis
        const groupOperation = groupBy(pesticidesFiltered, p => `${p.qualitometer}#${p.operation}`)
        const sumByOperation = keys(groupOperation).map(key => {
            const result = sumBy(groupOperation[key], 'result')
            return {
                result,
                value: `${round(result, 4)} (${i18n.sum})`,
                color: checkContamination ? PASTEL_COLOR.orange : PASTEL_COLOR.blue,
                qualitometer: groupOperation[key][0]?.qualitometer,
            }
        })
        const maxSumSubstance = maxBy(sumByOperation, 'result')
        if (consideredCalculationMethods.includes(MAX_SUM) && (maxSumSubstance?.result >= thresholdPesticides2 || !checkContamination)) return maxSumSubstance
        const sumAnalysis = sumByOperation[0]
        const result = meanBy(sumByOperation, 'result')
        return {
            result,
            value: `${round(result, 4)} (${i18n.sum})`,
            color: checkContamination ? PASTEL_COLOR.orange : PASTEL_COLOR.blue,
            qualitometer: sumAnalysis?.qualitometer,
        }
    }
    return maxBy(pesticidesAnalysis, 'analysisDate')
}

const buildPesticidesProblematicHelper = (calculationMethod) => {
    const base = [
        calculationMethod.includes(MAX_SUBSTANCE) ? `${i18n.maxMaxSubstance} ; ` : '',
        calculationMethod.includes(AVERAGE_SUBSTANCE) ? `${i18n.pesticidesAverageMax} ; ` : '',
        calculationMethod.includes(MAX_SUM) ? `${i18n.maxSumPesticides} ; ` : '',
        calculationMethod.includes(AVERAGE_SUM) ? `${i18n.averageSumPesticides}` : '',
    ].filter(e => e !== '')
    if (base.length === 0) return `${i18n.any} : ${i18n.pesticidesAverageMax} ${i18n.and} ${i18n.maxSumPesticides} ${i18n.displayByDefault}`
    const wholeString = base.join(' ')
    if (wholeString.endsWith(' ; ')) return wholeString.slice(0, wholeString.length - 3)
    return wholeString
}

const PesticidesOverviewMap = ({
    analysis = [],
    qualitoIds = [],
    catchments = [],
    catchmentPoints = [],
    thresholdList = {},
}) => {
    const dispatch = useDispatch()

    const {
        qualitometers,
    } = useSelector(store => ({
        qualitometers: store.QualityReducer.qualitometersLight,
    }), shallowEqual)

    const endPeriod = useEndPeriod(intParser)
    const regroupment = useApplicationSetting('CATCHMENT_REGROUPING', setting => setting ? parseInt(setting) : DEFAULT_REGROUPMENT)
    const thresholdPesticides1 = useApplicationSetting('CATCHMENT_PESTICIDE_THRESHOLD_1', setting => setting ? parseFloat(setting) : DEFAULT_THRESHOLD_PESTICIDES_1)
    const thresholdPesticides2 = useApplicationSetting('CATCHMENT_PESTICIDE_THRESHOLD_2', setting => setting ? parseFloat(setting) : DEFAULT_THRESHOLD_PESTICIDES_2)
    const distributionThreshold1 = useApplicationSetting('CATCHMENT_PESTICIDES_DISTRIBUTION_THRESHOLD_1', floatParser)
    const distributionThreshold2 = useApplicationSetting('CATCHMENT_PESTICIDES_DISTRIBUTION_THRESHOLD_2', floatParser)
    const distributionSumThreshold1 = useApplicationSetting('CATCHMENT_SUM_PESTICIDES_DISTRIBUTION_THRESHOLD_1', floatParser)
    const distributionSumThreshold2 = useApplicationSetting('CATCHMENT_SUM_PESTICIDES_DISTRIBUTION_THRESHOLD_2', floatParser)
    const listSumPesticides = useApplicationSetting('CATCHMENT_LIST_SUM_PESTICIDES', listStringParser)
    const calculationMethod = useApplicationSetting('CATCHMENT_OVERVIEW_CALCULATION_METHOD', listStringParser)

    const {
        value: isLoaded,
        setTrue: loaded,
    } = useBoolean(false)

    const [mapView, setMapView] = useState(1)
    const [criteriaValue, setCriteriaValue] = useState(0)
    const [catchmentsFiles, setCatchmentsFiles] = useState([])

    const mapViews = [
        { id: VIEW.CATCHMENTS, name: i18n.catchments },
        { id: VIEW.QUALITOMETERS, name: i18n.qualitometers },
    ]

    const distributionThresholds = useMemo(() => {
        const settingThresholds = [distributionThreshold1, distributionThreshold2].filter(t => !isUndefined(t))
        return settingThresholds.length ? settingThresholds : DEFAULT_PESTICIDES_DISTRIBUTION_THRESHOLDS
    }, [distributionThreshold1, distributionThreshold2])
    const thresholdsColors = useMemo(() => (THRESHOLD_COLOR_BY_SIZE[distributionThresholds.length + 1]), [distributionThresholds.length])
    const distributionSumThresholds = useMemo(() => {
        const settingSumThresholds = [distributionSumThreshold1 ?? distributionThreshold1, distributionSumThreshold2 ?? distributionThreshold2].filter(t => !isUndefined(t))
        return settingSumThresholds.length ? settingSumThresholds : DEFAULT_SUM_PESTICIDES_DISTRIBUTION_THRESHOLDS
    }, [distributionSumThreshold1, distributionThreshold1, distributionSumThreshold2, distributionThreshold2])

    const getName = useCallback(i => {
        if (i === 0) {
            return `${i18n.respectThreshold} (${i18n.maxByPesticides}) ${i18n.and} ${distributionSumThresholds[i]} μg/l (${i18n.sum})`
        }
        return `${i18n.exceedingThreshold} (${i18n.maxByPesticides}) ${i18n.and}/${i18n.or} ${distributionSumThresholds[i - 1]} μg/l (${i18n.sum})`
    }, [distributionSumThresholds])

    const criterias = useMemo(() => ([
        { value: CRITERIA.PROBLEMATIC, label: <ViewFormatter
            title={i18n.displayByProblematic}
            helper={template(i18n.pesticidesProblematicDetail)({ methods: buildPesticidesProblematicHelper(calculationMethod) })}
            labels={[i18n.pesticides, i18n.noContaminationHigherThanThreshold, i18n.missingData]}
            colors={[PASTEL_COLOR.orange, PASTEL_COLOR.blue, disabledBorderInputColor]}
        /> },
        { value: CRITERIA.THRESHOLDS, label: <ViewFormatter
            title={`${i18n.displayByThreshold} (${i18n.pesticides})`}
            helper={i18n.pesticidesThresholdDetail}
            labels={[getName(0), getName(1), getName(2), i18n.missingData]}
            colors={[...thresholdsColors, disabledBorderInputColor]}
        /> },
    ]), [thresholdsColors, getName, calculationMethod])

    const contaminationPerAnalysis = useMemo(() => {
        const optionsCalculStatePerAnalysis = {
            thresholdPesticides1,
            thresholdPesticides2,
            threshold: thresholdList,
            listSumPesticides,
            calculationMethod: calculationMethod.filter(m => m === AVERAGE_SUBSTANCE || m === MAX_SUBSTANCE),
        }
        return analysis.map(a => ({
            ...a,
            contamination: getPesticidesState([a], optionsCalculStatePerAnalysis),
            thresholdExceeding: getAnalysisThresholdExceeding(a, distributionThresholds, thresholdList),
        }))
    }, [distributionThresholds, thresholdList, analysis, calculationMethod, thresholdPesticides1, thresholdPesticides2, listSumPesticides])
    const usedThresholds = useMemo(() => {
        if (criteriaValue === CRITERIA.PROBLEMATIC) {
            return {
                thresholds: [i18n.pesticides, i18n.noContaminationHigherThanThreshold, i18n.missingData],
                colors: [PASTEL_COLOR.orange, PASTEL_COLOR.blue, disabledBorderInputColor],
            }
        }
        if (criteriaValue === CRITERIA.THRESHOLDS) {
            return {
                thresholds: ['noData', 'threshold0', 'threshold1', 'threshold2'],
                colors: [disabledBorderInputColor, ...thresholdsColors],
            }
        }
        return []
    }, [criteriaValue, thresholdsColors])
    const formattedAnalysis = useMemo(() => analysisFormatter(contaminationPerAnalysis, usedThresholds), [contaminationPerAnalysis, usedThresholds])
    const pesticidesAnalysisGroup = useMemo(() => groupBy(contaminationPerAnalysis, STATION_TYPE_NAME.qualitometer), [contaminationPerAnalysis])

    const contaminationPerStation = useMemo(() => {
        const optionsCalculStatePerStation = {
            thresholdPesticides1,
            thresholdPesticides2,
            threshold: thresholdList,
            listSumPesticides,
            calculationMethod,
        }
        return qualitoIds.map(id => {
            const qualitometer = qualitometers.find(q => q.id === id)
            const pesticides = pesticidesAnalysisGroup[id] || []

            if (pesticides.length === 0) {
                return { ...qualitometer, thresholdExceeding: 'noData', contamination: getPesticidesState(pesticides, optionsCalculStatePerStation) }
            }
            const quantifiedAnalysis = pesticides.filter(a => a.remark === REMARK_CODES.VALID)
            if (quantifiedAnalysis.length === 0) {
                return {
                    ...qualitometer,
                    thresholdExceeding: 'threshold0',
                    thresholdAnalysis: searchMaxAnalysis(pesticides),
                    contamination: getPesticidesState(pesticides, optionsCalculStatePerStation),
                }
            }

            const maxAnalysis = maxBy(quantifiedAnalysis, 'result')
            const paramThreshold = thresholdList.thresholds?.find(t => t.parameter === maxAnalysis.parameter)
            const thresholdsToTest = paramThreshold ? [
                paramThreshold.threshold1 ?? distributionThresholds[0],
                paramThreshold.threshold2 ?? distributionThresholds[1],
            ].filter(t => !isUndefined(t)) : distributionThresholds
            const indiceResult = thresholdsToTest.findIndex(t => maxAnalysis.result < t)

            const sumAnalysis = listSumPesticides.length ? quantifiedAnalysis.filter(a => listSumPesticides.includes(a.parameter)) : quantifiedAnalysis
            const groupOperation = groupBy(sumAnalysis, p => `${p.qualitometer}#${p.operation}`)
            const sumByGroup = keys(groupOperation).map(key => ({
                result: sumBy(groupOperation[key], 'result'),
                value: `${round(sumBy(groupOperation[key], 'result'), 4)}`,
                operation: groupOperation[key][0]?.operation,
                qualitometer: groupOperation[key][0]?.qualitometer,
            }))
            const maxSum = maxBy(sumByGroup, 'result')
            const indiceSum = distributionSumThresholds.findIndex(t => maxSum.result < t)

            const indice = max([
                indiceResult === -1 ? thresholdsToTest.length : indiceResult,
                indiceSum === -1 ? distributionSumThresholds.length : indiceSum,
            ])
            const thresholdIndice = indice !== -1 ? `threshold${indice}` : `threshold${distributionThresholds.length}`

            return {
                ...qualitometer,
                thresholdExceeding: thresholdIndice,
                thresholdAnalysis: indice === indiceResult || indiceResult === -1 ? maxAnalysis : maxSum,
                thresholdColor: thresholdColor(thresholdIndice, thresholdsColors, disabledBorderInputColor),
                contamination: getPesticidesState(pesticides, optionsCalculStatePerStation),
            }
        })
    }, [qualitoIds, qualitometers, pesticidesAnalysisGroup, distributionSumThresholds, distributionThresholds, thresholdsColors, thresholdList, listSumPesticides, calculationMethod, thresholdPesticides1, thresholdPesticides2])

    const filteredAnalysis = useMemo(() => {
        const unusedQualitometers = qualitoIds.filter(id => !analysis.some(a => a.qualitometer === id))
        const ghostAnalysis = unusedQualitometers.map(q => ({ qualitometer: q, result: -1, value: NO_ANALYSIS_VALUE, color: disabledBorderInputColor }))
        const mapAnalysis = [...formattedAnalysis, ...ghostAnalysis]
        return orderBy(mapAnalysis, 'analysisDate', 'desc')
    }, [analysis, qualitoIds, formattedAnalysis])

    const shownAnalysis = useMemo(() => contaminationPerStation.map(s => {
        const stationAnalysis = (pesticidesAnalysisGroup[s.id] || []).filter(a => a.remark === REMARK_CODES.VALID)
        if (s.contamination === MISSING_DATA) return { qualitometer: s.id, result: -1, value: NO_ANALYSIS_VALUE, color: disabledBorderInputColor }
        if (criteriaValue === CRITERIA.THRESHOLDS) {
            return { ...s.thresholdAnalysis, color: s.thresholdColor }
        }
        if (s.contamination === NO_CONTAMINATION) {
            return findAnalysisByContamination(false, stationAnalysis, calculationMethod, listSumPesticides)
        }
        if (s.contamination === PESTICIDES) {
            return findAnalysisByContamination(true, stationAnalysis, calculationMethod, listSumPesticides, thresholdList, thresholdPesticides1, thresholdPesticides2)
        }
        return maxBy(stationAnalysis, 'analysisDate')
    }), [contaminationPerStation, pesticidesAnalysisGroup, thresholdPesticides1, thresholdPesticides2, listSumPesticides, criteriaValue, calculationMethod, thresholdList])

    useEffect(() => {
        const sandreCatchments = catchments.filter(c => !isUndefined(c.sandreCode))
        const uniqueSandre = uniq(sandreCatchments.map(c => c.sandreCode)).filter(s => !catchmentsFiles.some(f=>f?.url?.includes(`cdaac_${s}.kml`)))
        const filesPromises = uniqueSandre.map(s => dispatch(CatchmentAction.fetchKML(`cdaac_${s}`)))
        Promise.all(filesPromises)
            .then((files = []) => {
                if (!isUndefined(files)) {
                    const sandreFiles = compact(files).map(f => ({ ...f, sandreCode: f.name?.split('_')?.[1]?.split('.kml')?.[0] }))
                    const formattedFiles = sandreCatchments.reduce((acc, c) => {
                        const catchmentFile = sandreFiles.find(f => f.sandreCode === c.sandreCode?.toString())
                        if (isUndefined(catchmentFile?.name) || isUndefined(catchmentFile?.url)) return acc
                        const now = moment().valueOf()
                        const formattedName = getFormatedFileName(c.code ?? '', IS_ACTIVE, getFileName(catchmentFile.name), getFileExtension(catchmentFile.name), CARTO_MNEMONIQUE, now, STATION_TYPE_NAME.catchment)
                        if (files.some(file => file?.name === formattedName)) {
                            dispatch(ToastrAction.warning(i18n.nameAlreadyExist))
                            return acc
                        }
                        const formattedFile = {
                            code: c.id,
                            url: catchmentFile.url,
                            name: formattedName,
                            stationCode: c.code ?? '',
                        }
                        return [...acc, new DtoFile(formattedFile, DOCUMENT)]
                    }, [])
                    if (formattedFiles.length > 0) setCatchmentsFiles(formattedFiles)
                }
            })
            .finally(loaded)
    }, [catchments])

    const colorfulFiles = useMemo(() => catchmentsFiles.map(f => {
        const remainingQualitometers = qualitoIds.map(id => qualitometers.find(q => q.id === id))
        const catchment = catchments.find(c => c.id === f.code) || {}
        const points = catchmentPoints.filter(p => p.id === catchment.id)
        const qualito = remainingQualitometers.filter(q => points.map(p => p.codeWithoutDesignation).includes(q.code))
        const shownColor = shownAnalysis.find(a => qualito.map(q => q.id).includes(a.qualitometer))?.color
        return { ...f, fillColor: shownColor ?? whiteColor, strokeColor: blackColor }
    }), [catchments, catchmentPoints, qualitoIds, qualitometers, catchmentsFiles, shownAnalysis])

    const panelTitle = useMemo(() => {
        const startYear = getYear(moment(endPeriod, 'YYYY').subtract(regroupment - 1, 'years').startOf('year').valueOf())
        const endYear = getYear(moment(endPeriod, 'YYYY').endOf('year').valueOf())
        if (startYear === endYear) return `${i18n.problematicsPromotion} - ${i18n.year} ${startYear}`
        return `${i18n.problematicsPromotion} - ${i18n.periode} ${startYear}-${endYear}`
    }, [endPeriod, regroupment])
    const height = window.innerHeight / 2

    const sortingType = useMemo(() => {
        if (criteriaValue === CRITERIA.THRESHOLDS) return SORTING_TYPE.FIX
        if (calculationMethod.length === 0 || calculationMethod.length > 1) return SORTING_TYPE.FIX
        if (calculationMethod[0] === AVERAGE_SUBSTANCE) return SORTING_TYPE.AVERAGE
        if (calculationMethod[0] === MAX_SUBSTANCE) return SORTING_TYPE.MAX
        return SORTING_TYPE.FIX
    }, [calculationMethod, criteriaValue])

    return (
        <Box sx={{ height: `${height}px` }}>
            {isLoaded && (
                <CartographyPanel
                    layers={[mapView === VIEW.CATCHMENTS ? LAYERS.CATCHMENTS : LAYERS.STATIONS_POINTS]}
                    catchments={catchments}
                    analysis={filteredAnalysis}
                    fixedAnalysis={shownAnalysis}
                    qualityThresholds={thresholdList?.thresholds ?? []}
                    sortingType={sortingType}
                    stationsPanelTitle={mapView === VIEW.CATCHMENTS ? i18n.catchments : i18n.qualitometers}
                    panel={
                        <CriteriaPanel
                            title={panelTitle}
                            criteriaValue={criteriaValue}
                            onChangeCriteriaValue={setCriteriaValue}
                            mapView={mapView}
                            onChangeMapView={setMapView}
                            criterias={criterias}
                            views={mapViews}
                        />
                    }
                    height={height}
                    stationFiles={colorfulFiles}
                />
            ) || <ProgressCard indeterminate/>}
        </Box>
    )
}

PesticidesOverviewMap.propTypes = {
    analysis: PropTypes.arrayOf(PropTypes.instanceOf(DtoAnalysisUltraLight)),
    qualitoIds: PropTypes.arrayOf(PropTypes.number),
    catchments: PropTypes.arrayOf(PropTypes.instanceOf(CatchmentDto)),
    catchmentPoints: PropTypes.arrayOf(PropTypes.instanceOf(CatchmentPointDto)),
    thresholdList: PropTypes.shape({
        thresholds: PropTypes.arrayOf(QualityThresholdType),
    }),
}

const GlobalOverviewMap = ({
    analysis = [],
    qualitoIds = [],
    useP90 = false,
    catchments = [],
    catchmentPoints = [],
    thresholdList = {},
}) => {
    const dispatch = useDispatch()

    const {
        qualitometers,
    } = useSelector(store => ({
        qualitometers: store.QualityReducer.qualitometersLight,
    }), shallowEqual)

    const thresholdNitrates = useApplicationSetting('CATCHMENT_NITRATES_THRESHOLD', setting => setting ? parseFloat(setting) : DEFAULT_THRESHOLD_NITRATES)
    const thresholdPesticides1 = useApplicationSetting('CATCHMENT_PESTICIDE_THRESHOLD_1', setting => setting ? parseFloat(setting) : DEFAULT_THRESHOLD_PESTICIDES_1)
    const thresholdPesticides2 = useApplicationSetting('CATCHMENT_PESTICIDE_THRESHOLD_2', setting => setting ? parseFloat(setting) : DEFAULT_THRESHOLD_PESTICIDES_2)
    const endPeriod = useEndPeriod(intParser)
    const regroupment = useApplicationSetting('CATCHMENT_REGROUPING', setting => setting ? parseInt(setting) : DEFAULT_REGROUPMENT)

    const listSumPesticides = useApplicationSetting('CATCHMENT_LIST_SUM_PESTICIDES', listStringParser)
    const calculationMethod = useApplicationSetting('CATCHMENT_OVERVIEW_CALCULATION_METHOD', listStringParser)

    const {
        value: isLoaded,
        setTrue: loaded,
    } = useBoolean(false)

    const [mapView, setMapView] = useState(1)
    const [catchmentsFiles, setCatchmentsFiles] = useState([])

    const mapViews = [
        { id: VIEW.CATCHMENTS, name: i18n.catchments },
        { id: VIEW.QUALITOMETERS, name: i18n.qualitometers },
    ]

    const optionsCalculStatePerAnalysis = {
        thresholdNitrates,
        thresholdPesticides1,
        thresholdPesticides2,
        threshold: thresholdList,
        listSumPesticides,
        calculationMethod: calculationMethod.filter(m => m === AVERAGE_SUBSTANCE || m === MAX_SUBSTANCE),
        useP90,
    }

    const formattedNitratesAnalysis = useMemo(() => {
        const nitratesAnalysis = analysis.filter(({ parameter }) => parameter === NITRATES_CODE)
        const contaminationNitratesAnalysis = nitratesAnalysis.map(a => ({
            ...a,
            contamination: getNitratesState([a], optionsCalculStatePerAnalysis),
        }))
        return analysisFormatter(contaminationNitratesAnalysis, { thresholds: [thresholdNitrates], colors: [PASTEL_COLOR.blue, PASTEL_COLOR.yellow] })
    }, [analysis, thresholdNitrates, optionsCalculStatePerAnalysis])

    const formattedPesticidesAnalysis = useMemo(() => {
        const pesticidesAnalysis = analysis.filter(({ parameter }) => parameter !== NITRATES_CODE)
        const contaminationPesticidesAnalysis = pesticidesAnalysis.map(a => ({
            ...a,
            contamination: getPesticidesState([a], optionsCalculStatePerAnalysis),
        }))
        return analysisFormatter(
            contaminationPesticidesAnalysis,
            {
                thresholds: [i18n.noData, i18n.pesticides, i18n.nitrates, i18n.noContaminationHigherThanThreshold, i18n.nitratesAndPesticides],
                colors: [disabledBorderInputColor, PASTEL_COLOR.orange, PASTEL_COLOR.yellow, PASTEL_COLOR.blue, PASTEL_COLOR.red],
            },
        )
    }, [analysis, optionsCalculStatePerAnalysis])

    const nitratesAnalysisGroup = useMemo(() => groupBy(formattedNitratesAnalysis, STATION_TYPE_NAME.qualitometer), [formattedNitratesAnalysis])
    const pesticidesAnalysisGroup = useMemo(() => groupBy(formattedPesticidesAnalysis, STATION_TYPE_NAME.qualitometer), [formattedPesticidesAnalysis])

    const contaminationPerStation = useMemo(() => {
        const optionsCalculState = {
            thresholdNitrates,
            thresholdPesticides1,
            thresholdPesticides2,
            threshold: thresholdList,
            listSumPesticides,
            calculationMethod,
            useP90,
        }
        return qualitoIds.map(id => {
            const qualitometer = qualitometers.find(q => q.id === id)
            const nitrates = nitratesAnalysisGroup[id] || []
            const pesticides = pesticidesAnalysisGroup[id] || []
            return {
                ...qualitometer,
                contamination: getContaminationState(nitrates, pesticides, optionsCalculState),
            }
        })
    }, [thresholdNitrates, thresholdPesticides1, thresholdPesticides2, thresholdList, listSumPesticides, calculationMethod, useP90, qualitometers, nitratesAnalysisGroup, pesticidesAnalysisGroup, qualitoIds])

    const shownAnalysis = useMemo(() => contaminationPerStation.map(s => {
        const stationNitratesAnalysis = (nitratesAnalysisGroup[s.id] || []).filter(a => a.remark === REMARK_CODES.VALID)
        const stationPesticidesAnalysis = (pesticidesAnalysisGroup[s.id] || []).filter(a => a.remark === REMARK_CODES.VALID)

        switch (s.contamination) {
            case MISSING_DATA:
                return { qualitometer: s.id, result: -1, value: NO_ANALYSIS_VALUE, color: disabledBorderInputColor }
            case NITRATES:
                return useP90 ? calculatePercentile90([...orderBy(stationNitratesAnalysis, 'result')]) : maxBy(stationNitratesAnalysis, 'result')
            case PESTICIDES:
                return findAnalysisByContamination(true, stationPesticidesAnalysis, calculationMethod, listSumPesticides, thresholdList, thresholdPesticides1, thresholdPesticides2)
            case NO_CONTAMINATION:
                const pesticideAnalysis = findAnalysisByContamination(false, stationPesticidesAnalysis, calculationMethod, listSumPesticides)
                const nitrateAnalysis = (useP90) ? calculatePercentile90([...orderBy(stationNitratesAnalysis, 'result')]) : maxBy(stationNitratesAnalysis, 'result')
                return maxBy([pesticideAnalysis, nitrateAnalysis], 'result')
            case NITRATES_PESTICIDES:
                const ContaminatedPesticideAnalysis = findAnalysisByContamination(true, stationPesticidesAnalysis, calculationMethod, listSumPesticides, thresholdList, thresholdPesticides1, thresholdPesticides2)
                const ContaminatedNitrateAnalysis = (useP90) ? calculatePercentile90([...orderBy(stationNitratesAnalysis, 'result')]) : maxBy(stationNitratesAnalysis, 'result')
                return { ...maxBy([ContaminatedPesticideAnalysis, ContaminatedNitrateAnalysis], 'result'), color: PASTEL_COLOR.red, contamination: NITRATES_PESTICIDES }
            default:
                return maxBy([...stationNitratesAnalysis, ...stationPesticidesAnalysis], 'analysisDate')
        }
    }), [contaminationPerStation, thresholdPesticides1, thresholdPesticides2, listSumPesticides, calculationMethod, thresholdList, nitratesAnalysisGroup, pesticidesAnalysisGroup, useP90])

    const filteredAnalysis = useMemo(() => {
        const unusedQualitometers = qualitoIds.filter(id => !analysis.some(a => a.qualitometer === id))
        const ghostAnalysis = unusedQualitometers.map(q => ({ qualitometer: q, result: -1, value: NO_ANALYSIS_VALUE, color: disabledBorderInputColor }))
        const mapAnalysis = [...formattedNitratesAnalysis, ...formattedPesticidesAnalysis, ...ghostAnalysis]
        return orderBy(mapAnalysis, 'analysisDate', 'desc')
    }, [analysis, qualitoIds, formattedNitratesAnalysis, formattedPesticidesAnalysis])

    useEffect(() => {
        const sandreCatchments = catchments.filter(c => !isUndefined(c.sandreCode))
        const uniqueSandre = uniq(sandreCatchments.map(c => c.sandreCode)).filter(s => !catchmentsFiles.some(f=>f?.url?.includes(`cdaac_${s}.kml`)))
        const filesPromises = uniqueSandre.map(s => dispatch(CatchmentAction.fetchKML(`cdaac_${s}`)))
        Promise.all(filesPromises)
            .then((files = []) => {
                if (!isUndefined(files)) {
                    const sandreFiles = compact(files).map(f => ({ ...f, sandreCode: f.name?.split('_')?.[1]?.split('.kml')?.[0] }))
                    const formattedFiles = sandreCatchments.reduce((acc, c) => {
                        const catchmentFile = sandreFiles.find(f => f.sandreCode === c.sandreCode?.toString())
                        if (isUndefined(catchmentFile?.name) || isUndefined(catchmentFile?.url)) return acc
                        const now = moment().valueOf()
                        const formattedName = getFormatedFileName(c.code ?? '', IS_ACTIVE, getFileName(catchmentFile.name), getFileExtension(catchmentFile.name), CARTO_MNEMONIQUE, now, STATION_TYPE_NAME.catchment)
                        if (files.some(file => file?.name === formattedName)) {
                            dispatch(ToastrAction.warning(i18n.nameAlreadyExist))
                            return acc
                        }
                        const formattedFile = {
                            code: c.id,
                            url: catchmentFile.url,
                            name: formattedName,
                            stationCode: c.code ?? '',
                        }
                        return [...acc, new DtoFile(formattedFile, DOCUMENT)]
                    }, [])
                    if (formattedFiles.length > 0) setCatchmentsFiles(formattedFiles)
                }
            })
            .finally(loaded)
    }, [catchments])

    const colorfulFiles = useMemo(() => catchmentsFiles.map(f => {
        const remainingQualitometers = qualitoIds.map(id => qualitometers.find(q => q.id === id))
        const catchment = catchments.find(c => c.id === f.code) || {}
        const points = catchmentPoints.filter(p => p.id === catchment.id)
        const qualito = remainingQualitometers.filter(q => points.map(p => p.codeWithoutDesignation).includes(q.code))
        const shownColor = shownAnalysis.find(a => qualito.map(q => q.id).includes(a.qualitometer))?.color
        return { ...f, fillColor: shownColor ?? whiteColor, strokeColor: blackColor }
    }), [catchments, catchmentPoints, qualitoIds, qualitometers, catchmentsFiles, shownAnalysis])

    const panelTitle = useMemo(() => {
        const startYear = getYear(moment(endPeriod, 'YYYY').subtract(regroupment - 1, 'years').startOf('year').valueOf())
        const endYear = getYear(moment(endPeriod, 'YYYY').endOf('year').valueOf())
        if (startYear === endYear) return `${i18n.problematicsPromotion} - ${i18n.year} ${startYear}`
        return `${i18n.problematicsPromotion} - ${i18n.periode} ${startYear}-${endYear}`
    }, [endPeriod, regroupment])
    const height = window.innerHeight / 2

    return (
        <Box sx={{ height: `${height}px` }}>
            {isLoaded && (
                <CartographyPanel
                    layers={[mapView === VIEW.CATCHMENTS ? LAYERS.CATCHMENTS : LAYERS.STATIONS_POINTS]}
                    sortingType={SORTING_TYPE.FIX}
                    catchments={catchments}
                    stationFiles={colorfulFiles}
                    height={height}
                    stationsPanelTitle={mapView === VIEW.CATCHMENTS ? i18n.catchments : i18n.qualitometers}
                    analysis={filteredAnalysis}
                    fixedAnalysis={shownAnalysis}
                    qualityThresholds={thresholdList?.thresholds ?? []}
                    panel = {
                        <CriteriaPanel
                            title={panelTitle}
                            criteriaValue={0}
                            mapView={mapView}
                            onChangeMapView={setMapView}
                            criterias={[{
                                value: CRITERIA.PROBLEMATIC,
                                label: <ViewFormatter
                                    title={`${i18n.displayByProblematic} (${i18n.nitratesAndPesticides})`}
                                    helper={i18n.globalProblematicDetail}
                                    labels={[i18n.noData, i18n.pesticides, i18n.nitrates, i18n.noContaminationHigherThanThreshold, i18n.nitratesAndPesticides]}
                                    colors={[disabledBorderInputColor, PASTEL_COLOR.orange, PASTEL_COLOR.yellow, PASTEL_COLOR.blue, PASTEL_COLOR.red]}
                                />,
                            }]}
                            views={mapViews}
                        />
                    }
                />
            ) || (
                <ProgressCard indeterminate/>
            )}
        </Box>
    )
}

GlobalOverviewMap.propTypes = {
    analysis: PropTypes.arrayOf(PropTypes.instanceOf(DtoAnalysisUltraLight)),
    qualitoIds: PropTypes.arrayOf(PropTypes.number),
    useP90: PropTypes.bool,
    catchments: PropTypes.arrayOf(PropTypes.instanceOf(CatchmentDto)),
    catchmentPoints: PropTypes.arrayOf(PropTypes.instanceOf(CatchmentPointDto)),
    thresholdList: PropTypes.shape({
        thresholds: PropTypes.arrayOf(QualityThresholdType),
    }),
}

export { NitratesOverviewMap, PesticidesOverviewMap, GlobalOverviewMap }